import React, { useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Play, AlertCircle, Wrench, Target, Lock, ArrowRight } from 'lucide-react';
import { getExerciseById, exercises, isBaselineExercise } from '../data/exercises';
import AnatomyDiagram from '../components/AnatomyDiagram';
import { useTierState } from '../hooks/useTierState';

const ExerciseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const ex = getExerciseById(id);
  const { is_paid: isPaid, loading: tierLoading } = useTierState();
  const [showFullForFree] = useState(false); // reserved for future "preview" affordance

  // Where did the user come from? Check ?from= query param, then router state, then history.
  // Default fallback is /library so a deep-linked visit still has a home.
  const fromParam = new URLSearchParams(location.search).get('from');
  const fromState = location.state?.from;
  const backHref = fromParam || fromState || null; // null = use navigate(-1)
  const backLabel = (() => {
    if (backHref === '/strength') return 'Back to workout';
    if (backHref === '/library') return 'All exercises';
    if (backHref) return 'Back';
    return 'Back';
  })();

  const goBack = (e) => {
    if (backHref) return; // Link handles it
    e.preventDefault();
    navigate(-1);
  };

  if (!ex) return (
    <div className="min-h-screen bg-transparent text-enf-text-primary pt-32 px-6 text-center">
      <p className="text-enf-text-secondary">Exercise not found.</p>
      <Link to="/library" className="text-enf-accent-fg mt-3 inline-block">Back to library</Link>
    </div>
  );

  // G2 gate: free users (Egnite) can only see baseline exercises in full
  // detail. Non-baseline exercises render a paywall card with a "back to
  // library" + "see pricing" path. Defense-in-depth — Library.jsx already
  // shows a lock overlay on these cards, but a direct URL hit must also be
  // blocked.
  if (!tierLoading && !isPaid && !isBaselineExercise(ex) && !showFullForFree) {
    return (
      <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
        <div className="max-w-3xl mx-auto px-6">
          <Link to="/library" className="inline-flex items-center gap-2 text-enf-text-secondary hover:text-enf-text-primary text-sm mb-8 transition-colors" data-testid="exercise-back-link">
            <ArrowLeft className="w-4 h-4" /> All exercises
          </Link>
          <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-8 md:p-12 text-center" data-testid="exercise-detail-locked">
            <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-enf-accent/10 border border-enf-accent-fg/30 flex items-center justify-center">
              <Lock className="w-6 h-6 text-enf-accent-fg" />
            </div>
            <div className="text-xs uppercase tracking-[0.3em] font-semibold text-enf-accent-fg mb-2">Enferno feature</div>
            <h1 className="font-display text-3xl md:text-4xl tracking-wide leading-tight mb-3">{ex.name}</h1>
            <p className="text-enf-text-secondary text-sm leading-relaxed max-w-md mx-auto mb-6">
              This exercise is part of the full Enferno Library (300+ exercises). You're on Egnite — the free tier — with 80 fundamentals available.
            </p>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors"
              data-testid="exercise-paywall-cta"
            >
              See pricing <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
      <div className="max-w-6xl mx-auto px-6">
        {backHref ? (
          <Link to={backHref} data-testid="exercise-back-link" className="inline-flex items-center gap-2 text-enf-text-secondary hover:text-enf-text-primary text-sm mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" /> {backLabel}
          </Link>
        ) : (
          <button type="button" onClick={goBack} data-testid="exercise-back-link" className="inline-flex items-center gap-2 text-enf-text-secondary hover:text-enf-text-primary text-sm mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" /> {backLabel}
          </button>
        )}

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="px-2.5 py-1 bg-enf-accent rounded text-xs font-mono font-bold">#{String(ex.id).padStart(3, '0')}</span>
              <span className="text-xs uppercase tracking-[0.3em] text-enf-text-muted font-semibold">{ex.muscle} · Tier {ex.tier}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter">{ex.name}</h1>
            <p className="text-enf-text-secondary mt-2 max-w-xl">{ex.description}</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          {/* Video — gracefully degrade when youtube id is null (dead link cleaned up,
              awaiting replacement curation per backlog). */}
          <div className="lg:col-span-2 bg-enf-surface-deep border border-enf-surface-border rounded-xl overflow-hidden">
            <div className="relative aspect-video bg-enf-surface-mid">
              {ex.youtube ? (
                <iframe className="absolute inset-0 w-full h-full" src={`https://www.youtube.com/embed/${ex.youtube}`} title={ex.name} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                  <Play className="w-8 h-8 text-enf-text-muted mb-3" />
                  <div className="text-sm font-semibold text-enf-text-secondary">Demo video coming soon</div>
                  <div className="text-xs text-enf-text-muted mt-1 max-w-xs">
                    Lean on the instructions and form cues below while we curate a replacement.
                  </div>
                </div>
              )}
            </div>
            <div className="p-5 flex items-center gap-2 text-xs text-enf-text-muted">
              <Play className="w-3.5 h-3.5" /> {ex.youtube ? 'Curated demo · YouTube' : 'No demo · use instructions below'}
            </div>
          </div>

          {/* Anatomy */}
          <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3"><Target className="w-4 h-4 text-enf-accent-fg" /><div className="text-xs uppercase tracking-widest text-enf-text-secondary font-semibold">Muscles worked</div></div>
            <AnatomyDiagram primary={ex.primaryMuscles} secondary={ex.secondaryMuscles} />
            <div className="mt-4 space-y-2 text-xs">
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm" style={{ backgroundColor: 'var(--enf-anatomy-primary)' }} /><span className="text-enf-text-secondary">Primary: {ex.primaryMuscles.join(', ')}</span></div>
              {ex.secondaryMuscles.length > 0 && <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm" style={{ backgroundColor: 'var(--enf-anatomy-secondary)' }} /><span className="text-enf-text-secondary">Secondary: {ex.secondaryMuscles.join(', ')}</span></div>}
            </div>
          </div>

          {/* Instructions */}
          <div className="lg:col-span-2 bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-enf-text-secondary mb-4">Instructions</h3>
            <ol className="space-y-3">
              {ex.instructions.map((s, i) => (
                <li key={i} className="flex gap-3">
                  <span className="w-6 h-6 rounded-md bg-enf-accent flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                  <span className="text-enf-text-primary leading-relaxed pt-0.5">{s}</span>
                </li>
              ))}
            </ol>
            <div className="mt-6 p-4 bg-enf-accent/5 border border-enf-accent-fg/20 rounded-md">
              <div className="text-xs uppercase tracking-widest text-enf-accent-fg font-semibold mb-1">Coach's note</div>
              <p className="text-sm text-enf-text-primary">{ex.tips}</p>
            </div>
            {ex.combatives && ex.combativesNote && (
              <div className="mt-3 p-4 bg-enf-role-warning/5 border border-enf-role-warning/30 rounded-md" data-testid="combatives-note">
                <div className="text-xs uppercase tracking-widest text-enf-role-warning font-semibold mb-1 flex items-center gap-1.5">
                  <span>🔥</span> Combatives benefit
                </div>
                <p className="text-sm text-enf-text-primary">{ex.combativesNote}</p>
              </div>
            )}
          </div>

          {/* Sidebar info */}
          <div className="space-y-3">
            <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-5">
              <div className="text-xs uppercase tracking-widest text-enf-text-muted mb-3">Details</div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-enf-text-secondary">Difficulty</span><span className="font-semibold">{ex.difficulty}</span></div>
                <div className="flex justify-between"><span className="text-enf-text-secondary">Tier</span><span className="font-semibold text-enf-accent-fg">Tier {ex.tier}</span></div>
                <div className="flex justify-between"><span className="text-enf-text-secondary">Levels</span><span className="font-semibold capitalize">{ex.levels.join(', ')}</span></div>
              </div>
            </div>
            <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3"><Wrench className="w-3.5 h-3.5 text-enf-accent-fg" /><div className="text-xs uppercase tracking-widest text-enf-text-muted">Equipment</div></div>
              <div className="flex flex-wrap gap-1.5">
                {ex.equipment.length ? ex.equipment.map((e) => (
                  <span key={e} className="px-2 py-0.5 bg-enf-surface-mid rounded text-xs text-enf-text-secondary capitalize">{e.replace(/-/g, ' ')}</span>
                )) : <span className="text-xs text-enf-text-muted">Bodyweight</span>}
              </div>
            </div>
            {ex.contraindications.length > 0 && (
              <div className="bg-enf-surface-deep border border-enf-role-danger/20 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3"><AlertCircle className="w-3.5 h-3.5 text-enf-role-danger" /><div className="text-xs uppercase tracking-widest text-enf-role-danger font-semibold">Caution</div></div>
                <p className="text-xs text-enf-text-secondary">May aggravate: <span className="capitalize text-enf-role-danger">{ex.contraindications.join(', ')}</span>. Enferno auto-substitutes if flagged in your profile.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExerciseDetail;
